import React from 'react';

/**
 * The two glyphs the Exclude action draws, as inline `<svg>` — verdigris has no circle-slash and neither
 * allowlisted icon atom exposes one. Both take `stroke`/`fill` from `currentColor`.
 */

export interface ExcludeGlyphProps {
  /** Rendered width/height in px. */
  readonly size?: number;
}

/** Prohibited / "no entry" mark: a ring with a diagonal bar, matching the approved mock. */
export const ExcludeGlyph: React.FC<ExcludeGlyphProps> = ({ size = 16 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    focusable="false"
  >
    <circle cx="12" cy="12" r="9" />
    <line x1="5.6" y1="5.6" x2="18.4" y2="18.4" />
  </svg>
);

/**
 * Filled warning triangle for the "Heavy action." banner. Filled rather than stroked so it still reads at
 * 16px and does not disappear against the banner's pale fill.
 *
 * The exclamation is a COUNTER punched in the surrounding surface colour, so the caller must pass the
 * background it is drawn on. That colour is deliberately a required-ish prop rather than a literal inside
 * this file: hard-coding the banner's token here would leave the bang visibly the wrong colour the moment
 * the banner fill is retuned, and it would put chrome outside `styles/tokens.ts`.
 */
export interface WarningTriangleGlyphProps extends ExcludeGlyphProps {
  /** The surface the glyph sits on — used to punch out the exclamation mark. */
  readonly counterFill: string;
}

export const WarningTriangleGlyph: React.FC<WarningTriangleGlyphProps> = ({
  size = 16,
  counterFill
}) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="currentColor"
    aria-hidden="true"
    focusable="false"
  >
    <path d="M12 2.8 22.6 21H1.4L12 2.8Z" />
    <path d="M11 9h2v6h-2V9Zm0 7.4h2v2h-2v-2Z" fill={counterFill} />
  </svg>
);
